import { PlugZap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { setEmbeddedSession } from '../api/client';
import { CapabilitiesProvider } from '../components/CapabilitiesProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { TenantTimeProvider } from '../components/TenantTimeProvider';
import { DashboardSection, type DashboardTab } from '../screens/dashboard/DashboardSection';
import type { DashboardHostProps } from '../screens/dashboard/DashboardView';
import { DashboardEmbedBridge } from './bridge';
import type { EmbedInit } from './bridge';

/**
 * The operations dashboard as the dispatch dashboard frames it, at
 * `/dashboard-embed`.
 *
 * A route of its own rather than a tab on the Communications module, which is
 * how `/analytics` got framed. The two are not the same shape: an analytics tab
 * is a section of the agent's inbox and belongs beside the channels, while this
 * screen is meant to REPLACE the Flutter `/dashboard` page — the dashboard's
 * home, opened by people who may never touch Communications. Mounting it inside
 * `CommsModule` would put a socket, a conversation store and two phone
 * providers behind the company's landing page to render nine cards, and would
 * tie a `dashboard_all` reader's home screen to the module's own chrome.
 *
 * It is the `/help-widget` pattern instead: the shared `EmbedAuthBridge` under
 * its own prefix, and nothing past the handshake. See console/README.md, which
 * is the authority on the bridge.
 *
 * Website analytics rides along as the section's second tab, exactly as it
 * does standalone, reached framed as `?tab=analytics`. It is offered on the
 * agent's `analytics` feature like everywhere else, and the Communications
 * module keeps its own analytics tab — a dashboard that links there still lands.
 * Framed, that tab is the REPORTS: a host that names `settings` on INIT keeps
 * Install and settings on its own My Website → Analytics page, and every way
 * into them here asks it to open that page instead (`OPEN_SETTINGS`).
 *
 * The frame holds no session of its own — framing it grants nothing unless the
 * parent supplies a token, and tokens only ever flow parent → iframe.
 */
export function EmbedDashboard() {
  const [session, setSession] = useState<EmbedInit | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  /**
   * The host's half of the header — who is signed in, and their shortcuts —
   * and the ways back into its router.
   *
   * Held with the senders beside it so the screen takes ONE prop and cannot be
   * given a shortcut list with no way to act on it. A record or board opener
   * is handed over only when the host said on INIT that it listens for one;
   * otherwise the screen keeps its links, which still work.
   */
  const [host, setHost] = useState<DashboardHostProps | null>(null);
  // Seeded from `?tab=` so the host can open straight on analytics, then held
  // here: nobody sees a frame's address, and touching the iframe's history
  // would put tab switches on the HOST's Back button.
  const [tab, setTab] = useState<DashboardTab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'analytics' ? 'analytics' : 'operations',
  );

  useEffect(() => {
    const bridge = new DashboardEmbedBridge();
    bridge.onHost = (next) =>
      setHost({
        ...next,
        run: (id) => bridge.runAction(id),
        openRecord: next.opens.record ? (target, id) => bridge.openRecord(target, id) : null,
        openDispatch: next.opens.dispatch ? (filter) => bridge.openDispatch(filter) : null,
        openSettings: next.opens.settings ? (target) => bridge.openSettings(target) : null,
      });
    bridge.onInit = (init) => {
      setEmbeddedSession({
        tenant: init.tenant,
        getToken: bridge.getToken,
        onAuthFailed: () => bridge.notifyAuthFailed(),
      });
      setSession(init);
      setTheme(init.theme);
    };
    bridge.onTheme = setTheme;
    bridge.start();
    return () => {
      bridge.dispose();
      setEmbeddedSession(null);
    };
  }, []);

  if (!session) {
    return (
      <div className="db-embed-host" data-theme={theme}>
        <div className="db-centre db-waiting">
          <PlugZap size={26} />
          <p>
            This page runs inside the dispatch dashboard, which signs it in automatically. Opened it
            directly? Use <a href="/dashboard">the standalone console</a> instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="db-embed-host" data-theme={theme}>
      {/* No shell above it to catch a render crash, and a white iframe inside
          the dashboard is the least explicable of all the ways this can fail. */}
      <ErrorBoundary label="Operations">
        <TenantTimeProvider>
          <CapabilitiesProvider>
            <DashboardSection embedded host={host} tab={tab} onTab={setTab} />
          </CapabilitiesProvider>
        </TenantTimeProvider>
      </ErrorBoundary>
    </div>
  );
}
